// libs/ui/src/primitives/AdCard.tsx
import { type ReactElement } from 'react';
import { Pressable } from 'react-native';
import { XStack, YStack } from 'tamagui';
import { Card } from './Card';
import { Icon, type IconName } from './Icon';
import { Text } from './Text';
import { useThemeColors } from '../hooks/useThemeColors';

export type AdCardVariant = 'compact' | 'wide';

export interface AdCardProps {
  /** Material icon glyph rendered in the leading icon tile. */
  icon: IconName;
  /** Small line above the title — typically "Sponsored". */
  eyebrow: string;
  /** Headline text. */
  title: string;
  /** Optional supporting copy under the title. */
  subtitle?: string | undefined;
  /** Label rendered on the trailing CTA chip (e.g. "Browse eSIM plans"). */
  ctaLabel: string;
  /** Affiliate-disclosure microcopy. ALWAYS rendered (ADR-0065 + FTC). */
  disclosure: string;
  /** Tap handler. When omitted the card renders read-only. */
  onPress?: (() => void) | undefined;
  /** Close button handler. When omitted the dismiss control is hidden. */
  onDismiss?: (() => void) | undefined;
  /** Layout density. `wide` stretches to the full row width; `compact`
   *  is sized to share a row with a sibling card via the snake-grid. */
  variant?: AdCardVariant | undefined;
  testID?: string | undefined;
}

/**
 * Presentation-only ad/sponsored tile. Mirrors the `Card` press
 * affordance (ADR-0074) so keyboard / hover / press behavior is
 * consistent with PlaceReviewCard, JourneyAnchorCard, etc. No
 * provider, tracking, or consent — wire those at the consumer.
 *
 * Always renders the disclosure line (FTC + ADR-0065). The eyebrow is
 * caller-provided so the same primitive can carry "Sponsored",
 * "Suggested", or future variants without a prop explosion.
 */
export function AdCard({
  icon,
  eyebrow,
  title,
  subtitle,
  ctaLabel,
  disclosure,
  onPress,
  onDismiss,
  variant = 'compact',
  testID,
}: AdCardProps): ReactElement {
  const theme = useThemeColors();
  const isWide = variant === 'wide';

  return (
    <YStack position="relative">
      <Card
        variant={onPress ? 'pressable' : 'flat'}
        onPress={onPress}
        padding={14}
        gap={10}
        borderRadius={18}
        testID={testID}
        accessibilityLabel={`${eyebrow}. ${title}.${subtitle ? ` ${subtitle}.` : ''} ${ctaLabel}.`}
      >
        <XStack gap={12} alignItems={isWide ? 'flex-start' : 'center'} flex={1}>
          <YStack
            width={36}
            height={36}
            borderRadius={10}
            backgroundColor="$surfaceCanvas"
            alignItems="center"
            justifyContent="center"
          >
            <Icon name={icon} size="sm" tone="subdued" />
          </YStack>
          <YStack flex={1} minWidth={0} gap={2}>
            <Text variant="overline" numberOfLines={1}>
              {eyebrow}
            </Text>
            <Text variant="title" numberOfLines={isWide ? 2 : 1}>
              {title}
            </Text>
            {subtitle ? (
              <Text variant="caption" numberOfLines={isWide ? 3 : 1}>
                {subtitle}
              </Text>
            ) : null}
          </YStack>
          <Icon name="open-in-new" size="sm" tone="subdued" />
        </XStack>
        <XStack alignItems="center" justifyContent="space-between" gap={8}>
          <Text variant="caption" color="$textSubdued" numberOfLines={1}>
            {disclosure}
          </Text>
          <Text
            variant="label"
            color="$accentCerulean"
            numberOfLines={1}
            accessibilityRole="text"
          >
            {ctaLabel}
          </Text>
        </XStack>
      </Card>
      {onDismiss ? (
        <Pressable
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel="Dismiss sponsored suggestion"
          hitSlop={8}
          style={({ pressed }) => [
            {
              position: 'absolute',
              top: 6,
              right: 6,
              width: 28,
              height: 28,
              borderRadius: 14,
              alignItems: 'center',
              justifyContent: 'center',
            },
            pressed ? { opacity: 0.6 } : null,
          ]}
        >
          <Icon name="close" size="sm" color={theme.textSubdued} />
        </Pressable>
      ) : null}
    </YStack>
  );
}
